import React from 'react';
import { Download, Image as ImageIcon } from '@mui/icons-material';
import { Avatar, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Tabs, Tooltip, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import AdminSearchField from '../../../../components/admin/AdminSearchField';
import AdminSurface from '../../../../components/admin/AdminSurface';
import { formatLabelWithCode } from '../../../../utils/locationFormat';
import type { CountryStatesGroup, SimpleState } from '../../../../types/locations';
import {
  filterAndSortGames,
  formatGameCurrency,
  formatGameDate,
  formatPendingPluVerification,
  type GameTableFilter,
  type GameTableSortDirection,
  type GameTableSortKey,
} from '../mappers/gameTableMappers';
import type { Game } from '../../../../types/features/games';
import { getGameImageSource } from '../../../../utils/normalizers/gameImageUrl';
import { adminColors, adminRadii, adminTypography } from '../../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCardHeaderSx,
  adminCombineSx,
  adminEmptyStateSx,
  adminFormControlSx,
  adminTableSx,
  adminTabsSx,
} from '../../../../styles/adminComponentStyles';

interface GameTableProps {
  games: Game[];
  groupedStates: CountryStatesGroup[];
  selectedCountry: string;
  selectedStateId: string;
  statesForSelectedCountry: SimpleState[];
  statesLoading: boolean;
  gamesLoading: boolean;
  onCountryChange: (event: SelectChangeEvent<string>) => void;
  onStateChange: (event: SelectChangeEvent<string>) => void;
  onDownloadCsv: () => void;
  onUpdate: (game: Game) => void;
}

const filterTabs: Array<{ value: GameTableFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'current', label: 'Current' },
  { value: 'expired', label: 'Expired' },
  { value: 'upcoming', label: 'Upcoming' },
];

type GameTableColumn = {
  align?: 'left' | 'center' | 'right';
  label: string;
  width: string;
} & (
  { key: GameTableSortKey; sortable?: true }
  | { key: 'pending_plu_verification'; sortable: false }
);

const columns: GameTableColumn[] = [
  { key: 'game_id', label: 'Game Id', width: '10%' },
  { key: 'name', label: 'Game', width: '28%' },
  { key: 'cost_per_ticket', label: 'Price', align: 'right', width: '10%' },
  { key: 'tickets_per_pack', label: 'Tickets per Pack', align: 'right', width: '13%' },
  { key: 'pending_plu_verification', label: 'PLU Verification', align: 'center', sortable: false, width: '18%' },
  { key: 'dates', label: 'Dates (Start-End)', width: '21%' },
];

const PLU_PENDING_ICON_SRC = '/Icons/pendingIcon.svg';
const PLU_REVIEWED_ICON_SRC = '/Icons/riviewdicon.svg';

const pluVerificationStatusSx = {
  alignItems: 'center',
  display: 'inline-flex',
  height: 24,
  justifyContent: 'center',
  width: 24,
} as const;

const pluVerificationIconSx = {
  display: 'block',
  height: 22,
  objectFit: 'contain',
  width: 22,
} as const;

const renderGameDateRange = (startDate: string | null, endDate: string | null): React.ReactNode => {
  const startLabel = formatGameDate(startDate || '');
  const endLabel = formatGameDate(endDate || '');

  if (startLabel === '-' && endLabel === '-') {
    return '-';
  }

  return (
    <Box component="span" sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.75, whiteSpace: 'nowrap' }}>
      <Box component="span">{startLabel}</Box>
      <Box component="span" sx={{ ...adminTypography.helper, color: adminColors.subtleText, fontWeight: 400 }}>
        to
      </Box>
      <Box component="span">{endLabel}</Box>
    </Box>
  );
};

const GameTable: React.FC<GameTableProps> = ({
  games,
  groupedStates,
  selectedCountry,
  selectedStateId,
  statesForSelectedCountry,
  statesLoading,
  gamesLoading,
  onCountryChange,
  onStateChange,
  onDownloadCsv,
  onUpdate,
}) => {
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<GameTableFilter>('all');
  const [sort, setSort] = React.useState<{ key: GameTableSortKey; direction: GameTableSortDirection }>({
    key: 'game_id',
    direction: 'asc',
  });

  const visibleGames = React.useMemo(
    () => filterAndSortGames(games, search, filter, sort.key, sort.direction),
    [filter, games, search, sort.direction, sort.key],
  );

  const handleSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  }, []);

  const handleFilterChange = React.useCallback((_event: React.SyntheticEvent, nextFilter: GameTableFilter) => {
    setFilter(nextFilter);
  }, []);

  const handleSort = React.useCallback((key: GameTableSortKey) => {
    setSort((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'rgb(239, 246, 255)',
          borderTop: '1px solid rgba(163, 174, 208, 0.24)',
          borderRadius: '8px',
          marginBottom: '8px',
          paddingLeft: '12px',
          borderBottom: '1px solid rgba(163, 174, 208, 0.24)',
        }}
      >
        <Tabs value={filter} onChange={handleFilterChange} sx={adminTabsSx}>
          {filterTabs.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
      </Box>
      <AdminSurface sx={{ border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgb(34 43 69 / 8%)', overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Box sx={adminCombineSx(adminCardHeaderSx, {
            alignItems: { xs: 'stretch', lg: 'center' },
            flexDirection: { xs: 'column', lg: 'row' },
          })}>
            <AdminSearchField
              value={search}
              onChange={handleSearchChange}
              placeholder="Search game id, name, or PLU"
              sx={{ width: { xs: '100%', lg: 320 } }}
              inputProps={{ 'aria-label': 'Search games' }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl
              size="small"
              sx={adminCombineSx(adminFormControlSx, { minWidth: { xs: '100%', sm: 160 } })}
              disabled={statesLoading}
            >
              <InputLabel>Country</InputLabel>
              <Select value={selectedCountry} label="Country" onChange={onCountryChange}>
                {groupedStates.map((group) => (
                  <MenuItem key={group.country_code} value={group.country_code}>
                    {formatLabelWithCode(group.country_name, group.country_code)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={adminCombineSx(adminFormControlSx, { minWidth: { xs: '100%', sm: 180 } })}
              disabled={statesLoading || !selectedCountry}
            >
              <InputLabel>State</InputLabel>
              <Select value={selectedStateId} label="State" onChange={onStateChange}>
                {statesForSelectedCountry.map((state) => (
                  <MenuItem key={state.state_code} value={state.state_code}>
                    {formatLabelWithCode(state.state_name, state.state_code)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={onDownloadCsv}
              startIcon={<Download />}
              size="small"
              disabled={gamesLoading || games.length === 0}
              sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { minWidth: { xs: '100%', sm: 150 } })}
            >
              Download CSV
            </Button>
            </Stack>
          </Box>
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={adminCombineSx(adminTableSx, {
            minWidth: 1180,
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              padding: '8px 12px !important',
            },
            '& .MuiTableHead-root .MuiTableCell-head': {
              textTransform: 'uppercase',
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
              cursor: 'pointer',
              height: 42,
            },
          })}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const active = column.sortable !== false && sort.key === column.key;

                return (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sortDirection={active ? sort.direction : false}
                    sx={{ width: column.width }}
                  >
                    {column.sortable === false ? (
                      <Box
                        component="span"
                        sx={{
                          alignItems: 'center',
                          display: 'inline-flex',
                          justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start',
                          minHeight: 24,
                          width: '100%',
                        }}
                      >
                        {column.label}
                      </Box>
                    ) : (
                      <TableSortLabel
                        active={active}
                        direction={active ? sort.direction : 'asc'}
                        hideSortIcon={false}
                        onClick={() => handleSort(column.key)}
                        sx={{
                          justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start',
                          width: '100%',
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleGames.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ border: 0, p: 1.5 }}>
                  <Box sx={adminCombineSx(adminEmptyStateSx, { border: 0, minHeight: 80 })}>
                    <Typography className="AdminEmptyState-title">No games found</Typography>
                    <Typography className="AdminEmptyState-copy">Try another search or status filter.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              visibleGames.map((game) => {
                const imageSource = getGameImageSource(game);
                const pluVerificationLabel = formatPendingPluVerification(game.pending_plu_verification);
                const pluVerificationIconSrc = game.pending_plu_verification
                  ? PLU_PENDING_ICON_SRC
                  : PLU_REVIEWED_ICON_SRC;

                return (
                  <TableRow key={game.id} hover onClick={() => onUpdate(game)}>
                    <TableCell sx={{ width: '10%' }}>{game.game_id}</TableCell>
                    <TableCell sx={{ width: '28%' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {imageSource ? (
                          <Avatar
                            src={imageSource}
                            alt={game.name.toUpperCase()}
                            imgProps={{ referrerPolicy: 'no-referrer' }}
                            sx={{ width: 44, height: 44, borderRadius: adminRadii.sm, '& .MuiAvatar-img': { objectFit: 'cover' } }}
                          />
                        ) : (
                          <Avatar sx={{ width: 44, height: 44, bgcolor: adminColors.neutralBackground, color: adminColors.mutedText }}>
                            <ImageIcon fontSize="small" />
                          </Avatar>
                        )}

                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                            {game.name}
                          </Typography>
                          <Typography sx={{ ...adminTypography.helper, color: adminColors.subtleText }}>
                            PLU: {game.plu || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell align="right" sx={{ width: '10%' }}>
                      {formatGameCurrency(game.cost_per_ticket)}
                    </TableCell>

                    <TableCell align="right" sx={{ width: '13%' }}>
                      {game.tickets_per_pack}
                    </TableCell>

                    <TableCell align="center" sx={{ width: '18%' }}>
                      <Tooltip title={pluVerificationLabel} arrow>
                        <Box
                          component="span"
                          aria-label={pluVerificationLabel}
                          sx={pluVerificationStatusSx}
                        >
                          <Box
                            component="img"
                            src={pluVerificationIconSrc}
                            alt=""
                            aria-hidden="true"
                            sx={pluVerificationIconSx}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ width: '21%' }}>
                      {renderGameDateRange(game.start_date, game.end_date)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </AdminSurface>
    </>
  );
};

export default React.memo(GameTable);
